import * as angular from 'angular';

import { FlamingoResponse } from '@mode/shared/contract-common';
import { readableBytes, HelpDocsService } from '@mode/shared/util-js';
import { MixableBaseController } from '~/common/component';
import { AppStore } from '~/types/main.types';
import { formatBytes } from '../../common/format.bytes.helper';
import { rawNumber } from '../../lib/axel/format/number.format';
import ViewService from '../../services/view-service';

const bytesFormatter = rawNumber(null, { thousands: ',' });

class FlamingoErrorController extends MixableBaseController {
  static $inject = ['ViewService', 'RxStoreService', 'FeatureFlagsFacade', 'HelpDocsService'];
  private error: FlamingoResponse.FlamingoErrorResponse;
  private isUnknownError = true;
  private errorType: string;

  workspaceTitle = 'Workspace';

  constructor(private viewService: ViewService, private rxStoreService: AppStore, public helpDocs: HelpDocsService) {
    super();
  }

  $onInit() {
    const failure = this.error;
    if (failure != null && 'result' in failure && 'stateProperties' in failure.result) {
      const failureCode =
        'failureCode' in failure.result.stateProperties
          ? failure.result.stateProperties.failureCode
          : failure.result.stateProperties.code;

      this.errorType =
        failureCode in FlamingoResponse.DatasetErrors ? failureCode : FlamingoResponse.DatasetErrors.UNKNOWN_ERROR;
      this.isUnknownError = this.errorType === FlamingoResponse.DatasetErrors.UNKNOWN_ERROR;
    } else if (failure.type === FlamingoResponse.ResponseType.SizeLimit) {
      this.isUnknownError = false;
      this.errorType = FlamingoResponse.ResponseType.SizeLimit;
    } else {
      this.isUnknownError = true;
    }
  }

  goToNotebook() {
    this.viewService.toggleNotebookShown(true);
    this.viewService.goToNotebook();
  }

  readableBytes(size: number): string {
    return readableBytes(size);
  }

  formatNumber(num: number) {
    return bytesFormatter(num);
  }

  get formattedByteLimit(): string {
    if (this.error.type === FlamingoResponse.ResponseType.SizeLimit) {
      return formatBytes(this.error.limit);
    }
  }

  get formattedByteSize(): string {
    if (this.error.type === FlamingoResponse.ResponseType.SizeLimit) {
      return formatBytes(this.error.actual);
    }
  }

  get formattedOverageSize(): string {
    if (this.error.type === FlamingoResponse.ResponseType.SizeLimit) {
      return formatBytes(this.error.actual - this.error.limit);
    }
  }
}

export const FlamingoErrorComponent: angular.IComponentOptions = {
  controller: FlamingoErrorController,
  controllerAs: 'ctrl',
  bindings: {
    error: '<',
    csvLink: '<',
  },
  template: `
    <div class="error-container">
      <div class="generic-error" ng-if="ctrl.isUnknownError">
        <div><i class="generic-error-icon icon-broken-viz"></i></div>
        <h4>Sorry, your query was successful, but we ran into problems displaying the data.</h4>
        <div>You can still access the dataset by downloading a csv <a ng-href="{{ctrl.csvLink}}" ng-click="$event.stopPropagation()">export</a>.</div>
  </div>

      <div class="detailed-error" ng-if="!ctrl.isUnknownError">
        <div class="error-header" ng-if="ctrl.errorType !== '${FlamingoResponse.ResponseType.SizeLimit}'">
          <i class="fail-icon icon-fail-color"></i>Sorry, we can't return your data for the following reasons:
        </div>
        <div class="error-header" ng-if="ctrl.errorType === '${FlamingoResponse.ResponseType.SizeLimit}'">
          Sorry, your query was successful, but we can't display or chart your data for the following reasons:
        </div>

        <div class="error-message-container">
          <div class="error-message" ng-if="ctrl.errorType === '${FlamingoResponse.ResponseType.SizeLimit}'">
            Your query results (<span class="byte-size" ng-bind="ctrl.formattedByteSize" />)
            are <span ng-bind="ctrl.formattedOverageSize" />
            over your {{ ctrl.workspaceTitle }}'s limit of <span ng-bind="ctrl.formattedByteLimit" />
            <a class="btn btn-secondary pull-right" href="{{ ctrl.helpDocs.links['https://mode.com/help/articles/querying-data/?utm_source=result_limit_cta#sorry,-this-data-is-larger-than-your-limit'] }}" target="_blank">
              Learn more
            </a>
          </div>
          <div class="error-message" ng-if="ctrl.errorType === '${FlamingoResponse.DatasetErrors.TOO_MANY_COLUMNS}'">
            Your query returns {{ctrl.error.result.stateProperties.columnCount}} columns, which exceeds our limit of {{ctrl.error.result.stateProperties.limit}}.
          </div>
          <div class="error-message" ng-if="ctrl.errorType === '${FlamingoResponse.DatasetErrors.COLUMN_TOO_WIDE}'">
            Your data contains one or more columns that exceed our column size limit of 1MB. Here's a summary of your columns and the approximate size of the data within them:
            <ul>
              <li ng-repeat="col in ctrl.error.result.stateProperties.columns | orderBy:'col.width':true | limitTo:5 track by $index">
                <span class="column-name">{{col.name}} </span><span class="column-size">({{ctrl.readableBytes(col.width)}})</span>
              </li>
              <div ng-if="ctrl.error.result.stateProperties.columns.length > 5">and {{ctrl.error.result.stateProperties.columns.length - 5}} more</div>
            </ul>
          </div>
          <div class="error-message" ng-if="ctrl.errorType === '${FlamingoResponse.DatasetErrors.ROW_TOO_WIDE}'">
            Your data contains one or more rows that exceed our row size limit of 2MB. Here's a summary of your columns and the approximate size of the data within them:
            <ul>
              <li ng-repeat="col in ctrl.error.result.stateProperties.columns | orderBy:'col.width':true | limitTo:5 track by $index">
                <span class="column-name">{{col.name}} </span><span class="column-size">({{ctrl.readableBytes(col.width)}})</span>
              </li>
              <div ng-if="ctrl.error.result.stateProperties.columns.length > 5">and {{ctrl.error.result.stateProperties.columns.length - 5}} more</div>
            </ul>
          </div>
          <div class="error-message" ng-if="ctrl.errorType === '${FlamingoResponse.DatasetErrors.DATA_PROBLEM}'">
            The following rows exceed our size limits:
            <ul>
              <li ng-repeat="problem in ctrl.error.result.stateProperties.dataProblems | limitTo:5 track by $index">
                Row <span class="row-number">{{problem.rowNumber}}</span> (occurring at column <span class="column-name">{{problem.attributeName}}</span>)
              </li>
              <div ng-if="ctrl.error.result.stateProperties.dataProblems.length > 5">and {{ctrl.error.result.stateProperties.dataProblems.length - 5}} more</div>
            </ul>
          </div>
        </div>
        <div class="action-buttons">
          <div>Try editing your query, or access your data in another way:</div>
          <a ng-href="{{ctrl.csvLink}}" ng-click="$event.stopPropagation()">
            <button class="btn query-run-action"><i class="csv-icon icon-export-mini-gray"></i>Export to CSV</button>
          </a>
          <button ng-click="ctrl.goToNotebook()" class="btn query-run-action">Go to Notebook</button>
        </div>
        <!-- TODO: Update with URL -->
        <!-- <div class="learn-more"><a href="#">Learn more</a> about data limits.</div> -->
      </div>

    </div>
  `,
};
